import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { aggregateWithFallback, localModeFor } from './localAggregate';
import styles from './NativeVisuals.module.css';

/* What each template can be narrowed by. Only columns with more than one
   value and few enough to pick from: class, section and academic year each
   hold a single value across the whole table, and Attendance's late flag is
   set on 10 rows out of 63,303, so none of them are offered.

   `month` lists the months the data spans and filters from that month on.
   `select` lists the distinct values of a column.
   `boolean` is a yes/no column. */
export const FILTERS = {
  Academics: [
    { key: 'subject', type: 'select', column: 'subject', label: 'Subject' },
    { key: 'termname', type: 'select', column: 'termname', label: 'Term' },
    { key: 'evaluation', type: 'select', column: 'evaluation', label: 'Assessment' }
  ],
  Attendance: [
    { key: 'from', type: 'month', column: 'date', label: 'From' },
    {
      key: 'attended',
      type: 'boolean',
      column: 'attended',
      label: 'Marked',
      trueLabel: 'Present',
      falseLabel: 'Absent'
    }
  ],
  Admissions: [
    { key: 'from', type: 'month', column: 'enquirydate', label: 'From' },
    { key: 'sourcename', type: 'select', column: 'sourcename', label: 'Source' },
    { key: 'stagename', type: 'select', column: 'stagename', label: 'Stage' },
    { key: 'status', type: 'select', column: 'status', label: 'Status' },
    {
      key: 'iscancelled',
      type: 'boolean',
      column: 'iscancelled',
      label: 'Cancelled',
      trueLabel: 'Cancelled only',
      falseLabel: 'Live only'
    }
  ],
  Receivables: [
    { key: 'feetype', type: 'select', column: 'feetype', label: 'Fee type' },
    { key: 'installmentname', type: 'select', column: 'installmentname', label: 'Installment' },
    { key: 'from', type: 'month', column: 'installmentdate', label: 'Due from' }
  ],
  Payments: [
    { key: 'mode', type: 'select', column: 'mode', label: 'Method' },
    { key: 'from', type: 'month', column: 'paymentdate', label: 'Paid from' }
  ]
};

const ALL = '';

/** Turns the chosen values into the filter list the query layer expects. */
export function toQueryFilters(template, values) {
  const out = [];
  (FILTERS[template] || []).forEach((def) => {
    const value = values[def.key];
    if (!value) return;
    if (def.type === 'month') out.push({ column: def.column, operator: 'gte', value });
    else if (def.type === 'boolean') {
      out.push({ column: def.column, operator: 'eq', value: value === 'yes' ? 'true' : 'false' });
    } else out.push({ column: def.column, operator: 'eq', value });
  });
  return out;
}

const monthName = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
};

/** Filter choices live at the page level so switching tabs keeps them. */
export function useFilterValues() {
  const [byTemplate, setByTemplate] = useState({});

  const set = useCallback((template, key, value) => {
    setByTemplate((prev) => ({
      ...prev,
      [template]: { ...(prev[template] || {}), [key]: value }
    }));
  }, []);

  const clear = useCallback((template) => {
    setByTemplate((prev) => ({ ...prev, [template]: {} }));
  }, []);

  return { byTemplate, set, clear };
}

/**
 * Sits above the tabs. Reads its choices once per template with no filters
 * applied, so picking one value never empties the other lists, and reports
 * how many rows the current selection matches.
 */
export const FilterPanel = ({ template, values, onChange, onClear }) => {
  const defs = useMemo(() => FILTERS[template] || [], [template]);
  const [options, setOptions] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [count, setCount] = useState(null);

  const queryFilters = useMemo(() => toQueryFilters(template, values), [template, values]);
  const filterKey = JSON.stringify(queryFilters);

  // Choices, read once per template.
  useEffect(() => {
    const lists = defs.filter((def) => def.type !== 'boolean');
    if (lists.length === 0) {
      setOptions({});
      setUnavailable(false);
      return undefined;
    }
    const controller = new AbortController();
    setOptions(null);
    setUnavailable(false);

    Promise.all(
      lists.map((def) =>
        aggregateWithFallback(
          {
            table: template,
            groupBy: [def.type === 'month' ? { column: def.column, bucket: 'month' } : def.column],
            metrics: [{ fn: 'count', as: 'n' }],
            orderBy:
              def.type === 'month'
                ? [{ column: def.column, direction: 'asc' }]
                : [{ column: 'n', direction: 'desc' }],
            limit: def.type === 'month' ? 400 : 200
          },
          { signal: controller.signal }
        )
      )
    )
      .then((results) => {
        if (controller.signal.aborted) return;
        const next = {};
        lists.forEach((def, i) => {
          next[def.key] = (results[i].rows || [])
            .map((row) => row[def.column])
            .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
            .map((v) => String(v));
        });
        setOptions(next);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setUnavailable(true);
        setOptions({});
      });

    return () => controller.abort();
  }, [template, defs]);

  // How many rows the current selection matches.
  useEffect(() => {
    const controller = new AbortController();
    setCount(null);
    aggregateWithFallback(
      {
        table: template,
        filters: JSON.parse(filterKey),
        metrics: [{ fn: 'count', as: 'n' }],
        limit: 1
      },
      { signal: controller.signal }
    )
      .then((result) => {
        if (controller.signal.aborted) return;
        const matched = Number((result.rows && result.rows[0] && result.rows[0].n) || 0);
        setCount({ matched, sample: result.sample || null });
      })
      .catch(() => {
        if (!controller.signal.aborted) setCount(null);
      });
    return () => controller.abort();
  }, [template, filterKey]);

  const active = defs.filter((def) => values[def.key]).length;
  const mode = localModeFor(template);
  const sampledSource = mode && mode.mode === 'sample';

  const readout = () => {
    if (!count) return <span className={styles.countPending}>counting…</span>;
    const { matched, sample } = count;
    if (sample) {
      // The count came from a sample, so scale it to say what the table holds.
      const estimate = Math.round((matched / Math.max(sample.shown, 1)) * sample.total);
      return (
        <>
          <strong className={styles.countValue}>~{estimate.toLocaleString()}</strong>
          <span className={styles.countUnit}>
            rows estimated ({matched.toLocaleString()} of {sample.shown.toLocaleString()} sampled)
          </span>
        </>
      );
    }
    return (
      <>
        <strong className={styles.countValue}>{matched.toLocaleString()}</strong>
        <span className={styles.countUnit}>
          rows match{matched > 5000 ? ' — narrow further to read every row' : ''}
        </span>
      </>
    );
  };

  if (defs.length === 0) return null;

  return (
    <div className={styles.filterPanel}>
      <div className={styles.filterPanelHead}>
        <span className={styles.filterPanelTitle}>Filters</span>
        <span className={styles.filterPanelTemplate}>{template}</span>
        {active > 0 ? (
          <button type="button" className={styles.filterClear} onClick={() => onClear(template)}>
            Clear {active === 1 ? 'filter' : `${active} filters`}
          </button>
        ) : null}
        <span className={styles.countBox}>{readout()}</span>
      </div>

      <div className={styles.filterBar}>
        {defs.map((def) => {
          const list = (options && options[def.key]) || [];
          return (
            <label key={def.key} className={styles.filterField}>
              <span className={styles.filterLabel}>{def.label}</span>
              <select
                className={styles.filterSelect}
                value={values[def.key] || ALL}
                onChange={(event) => onChange(template, def.key, event.target.value)}
                disabled={def.type !== 'boolean' && !options}
              >
                <option value={ALL}>
                  {def.type === 'month' ? 'The beginning' : 'All'}
                </option>
                {def.type === 'boolean' ? (
                  <>
                    <option value="yes">{def.trueLabel || 'Yes'}</option>
                    <option value="no">{def.falseLabel || 'No'}</option>
                  </>
                ) : (
                  list.map((option) => (
                    <option key={option} value={option}>
                      {def.type === 'month' ? monthName(option) : option}
                    </option>
                  ))
                )}
              </select>
            </label>
          );
        })}
      </div>

      {unavailable ? (
        <p className={styles.filterNote}>
          The choices for these lists could not be read, so only the yes/no filters work for
          now. The charts below explain why.
        </p>
      ) : null}
      {sampledSource && !unavailable ? (
        <p className={styles.filterNote}>
          {template} is too large to read in full, so the count above is estimated from a
          sample. Narrowing the filters will not change that until the aggregate endpoint is
          deployed — the server filters before sending, the browser cannot.
        </p>
      ) : null}
    </div>
  );
};
